/**
 * GestorCard.tsx — Card read-only de gestor para páginas de detalhe
 *
 * @internal true
 * @owner components/gestao
 * @updated 2026-06-19
 *
 * Componente compartilhado usado em EstadoDetalhe e MunicipioDetalhe
 * para exibir gestores com status, contato e botão de impersonate.
 */
import { Mail, Phone, LogIn } from 'lucide-react';

export interface GestorCardProps {
  user: {
    id: string;
    nome: string;
    email?: string;
    telefone?: string;
    fotoUrl?: string;
    ativo?: boolean;
    statusConta?: string;
  };
  onImpersonate: (id: string) => void;
  impersonating: string | null;
  /** Cor primária do avatar gradient (default: '#10b981') */
  accentColor?: string;
}

export function GestorCard({ user, onImpersonate, impersonating, accentColor = '#10b981' }: GestorCardProps) {
  const statusColor = user.statusConta === 'ATIVA' ? '#10b981' : user.statusConta === 'PENDENTE' ? '#f59e0b' : '#ef4444';
  const statusLabel = user.statusConta === 'ATIVA' ? 'Ativo' : user.statusConta === 'PENDENTE' ? 'Pendente' : user.statusConta || 'N/A';

  return (
    <div style={{
      padding: '16px 18px', borderRadius: 14,
      background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)',
      display: 'flex', alignItems: 'center', gap: 14,
      transition: 'all .2s', boxShadow: '0 2px 8px rgba(0,0,0,.04)',
    }}>
      {/* Avatar */}
      <div style={{
        width: 52, height: 52, borderRadius: 14, flexShrink: 0, overflow: 'hidden',
        background: user.fotoUrl ? `url(${user.fotoUrl}) center/cover` : `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontWeight: 800, fontSize: '1.2rem',
        border: `2px solid ${accentColor}25`,
      }}>
        {!user.fotoUrl && (user.nome?.charAt(0)?.toUpperCase() || '?')}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '.92rem', color: 'var(--text-main)', marginBottom: 3 }}>
          {user.nome}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {user.email && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.76rem', color: 'var(--text-muted)' }}>
              <Mail size={11} /> {user.email}
            </div>
          )}
          {user.telefone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.76rem', color: 'var(--text-muted)' }}>
              <Phone size={11} /> {user.telefone}
            </div>
          )}
        </div>
      </div>

      {/* Status + Action */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
        <span style={{
          padding: '3px 10px', borderRadius: 8, fontSize: '.65rem', fontWeight: 700,
          background: `${statusColor}15`, color: statusColor,
        }}>● {statusLabel}</span>
        {user.ativo && user.statusConta === 'ATIVA' && (
          <button onClick={() => onImpersonate(user.id)} disabled={impersonating === user.id} style={{
            padding: '5px 12px', fontSize: '.7rem', fontWeight: 700, borderRadius: 8,
            background: `${accentColor}10`, color: accentColor, border: `1px solid ${accentColor}25`, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
            opacity: impersonating === user.id ? .6 : 1,
          }}>
            <LogIn size={12} /> Entrar como
          </button>
        )}
      </div>
    </div>
  );
}
